import React, { useState, useEffect } from 'react';
import { api } from '@/lib/db';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';

const TEMPLATES = [
  {
    id: 'negative_review',
    label: 'Negatív értékelés értesítő',
    description: 'Akkor küldjük el, amikor 1–3 csillagos értékelés érkezik.',
    subjectKey: 'email_tmpl_negative_subject',
    bodyKey: 'email_tmpl_negative_body',
    defaultSubject: 'Új negatív értékelés érkezett – {{business_name}}',
    defaultBody: `Kedves Adminisztrátor!

Új negatív értékelés érkezett a(z) {{business_name}} vállalkozáshoz.

Helyszín: {{location_name}}
Értékelés: {{rating}} csillag
Értékelő neve: {{reviewer_name}}
E-mail: {{reviewer_email}}

Értékelés szövege:
"{{review_text}}"

Kérem, tekintse meg az értékelést az admin felületen.

Üdvözlettel,
SocialCard Review`,
    variables: [
      { key: '{{business_name}}', desc: 'Vállalkozás neve' },
      { key: '{{location_name}}', desc: 'Helyszín neve' },
      { key: '{{rating}}', desc: 'Csillag értékelés (1–3)' },
      { key: '{{reviewer_name}}', desc: 'Értékelő neve' },
      { key: '{{reviewer_email}}', desc: 'Értékelő e-mail' },
      { key: '{{review_text}}', desc: 'Értékelés szövege' },
    ],
  },
  {
    id: 'welcome_admin',
    label: 'Üdvözlő e-mail (új admin)',
    description: 'Akkor küldjük el, amikor új vállalkozást hoznak létre.',
    subjectKey: 'email_tmpl_welcome_subject',
    bodyKey: 'email_tmpl_welcome_body',
    defaultSubject: 'Üdvözöljük a SocialCard Review rendszerben – {{business_name}}',
    defaultBody: `Kedves {{admin_email}}!

Sikeresen létrehoztuk az Ön vállalkozói fiókját a SocialCard Review rendszerben.

Vállalkozás neve: {{business_name}}
Bejelentkezési e-mail: {{admin_email}}
Jelszó: {{admin_password}}

Az admin felület elérhető itt: https://review.socialcard.hu

Üdvözlettel,
SocialCard Review csapata`,
    variables: [
      { key: '{{business_name}}', desc: 'Vállalkozás neve' },
      { key: '{{admin_email}}', desc: 'Admin e-mail cím' },
      { key: '{{admin_password}}', desc: 'Kezdeti jelszó' },
    ],
  },
];

const EmailTemplates = () => {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState('negative_review');
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const cfg = await api.getConfig();
      setValues(cfg);
    } catch (e) {
      addToast('Hiba a betöltésnél', 'error');
    }
    setLoading(false);
  };

  const get = (key, fallback) => values[key] !== undefined ? values[key] : fallback;
  const set = (key, val) => setValues(prev => ({ ...prev, [key]: val }));

  const save = async (tmpl) => {
    setSaving(true);
    try {
      await api.setConfig(tmpl.subjectKey, get(tmpl.subjectKey, tmpl.defaultSubject));
      await api.setConfig(tmpl.bodyKey, get(tmpl.bodyKey, tmpl.defaultBody));
      addToast('Sablon mentve!', 'success');
    } catch (e) {
      addToast('Hiba a mentésnél', 'error');
    }
    setSaving(false);
  };

  const reset = (tmpl) => {
    if (!window.confirm('Visszaállítod az alapértelmezett sablonra?')) return;
    set(tmpl.subjectKey, tmpl.defaultSubject);
    set(tmpl.bodyKey, tmpl.defaultBody);
  };

  const active = TEMPLATES.find(t => t.id === activeTab);

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-3xl font-black text-gray-900">E-mail sablonok</h1>
        <p className="text-gray-500">Az automatikusan küldött e-mailek szövegének testreszabása.</p>
      </div>

      {loading ? (
        <div className="text-center py-16 text-gray-400">Betöltés...</div>
      ) : (
        <Card className="overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            {TEMPLATES.map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-5 py-3.5 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === t.id
                    ? 'border-blue-600 text-blue-600 bg-white'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {active && (
            <div className="p-6">
              <p className="text-sm text-gray-500 mb-6">{active.description}</p>
              <div className="grid grid-cols-3 gap-6">
                {/* Szerkesztő */}
                <div className="col-span-2 space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Tárgy</label>
                    <input
                      type="text"
                      value={get(active.subjectKey, active.defaultSubject)}
                      onChange={e => set(active.subjectKey, e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Üzenet szövege</label>
                    <textarea
                      rows={14}
                      value={get(active.bodyKey, active.defaultBody)}
                      onChange={e => set(active.bodyKey, e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button onClick={() => save(active)} disabled={saving}>
                      <SafeIcon name="Save" className="w-4 h-4 mr-2" />
                      {saving ? 'Mentés...' : 'Mentés'}
                    </Button>
                    <Button variant="outline" onClick={() => reset(active)}>
                      Visszaállítás
                    </Button>
                  </div>
                </div>

                {/* Változók súgó */}
                <div>
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                    <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <SafeIcon name="Info" className="w-4 h-4 text-blue-500" /> Elérhető változók
                    </h4>
                    <div className="space-y-3">
                      {active.variables.map(v => (
                        <div key={v.key}>
                          <code className="text-xs bg-white border border-gray-200 rounded px-1.5 py-0.5 text-blue-600 font-mono">
                            {v.key}
                          </code>
                          <p className="text-xs text-gray-500 mt-0.5 ml-0.5">{v.desc}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-4 pt-3 border-t border-gray-200 leading-relaxed">
                      A kapcsos zárójelben lévő változókat a rendszer automatikusan lecseréli küldéskor.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default EmailTemplates;
